// TODO: Change to auth module.
import { HttpBackend, HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { JwtHelperService } from '@auth0/angular-jwt'

import { environment } from '../../environments/environment'
import { catchError, firstValueFrom, throwError } from 'rxjs'
import { CookieService } from 'ngx-cookie-service'
import { Router } from '@angular/router'
import { Company } from './company.service'
import { EventData, EventsService } from './events.service'
import { NotificationService } from './notification.service'

interface Tokens {
  idToken: string
  accessToken: string
  expiresIn: number
  refreshToken: string
}

export interface LogInResponse {
  emailAddress: string
  userId: string
  tokens: Tokens
  userName: string
  isSuccess: boolean
  message: string | null
}

interface SignOutResponse {
  isSuccess: boolean
  message: string
}

interface ForgotAccountResponse {
  userId: string
  emailAddress: string
  isSuccess: boolean
  message: string
}

interface ForgotAccountCodeResponse {
  isSuccess: boolean
  message: string
}

interface ConfirmUserResponse {
  userId: string
  emailAddress: string
  isSuccess: boolean
  message: string
}

export interface UserProfileResponse {
  emailAddress: string
  emailVerified: string
  userId: string
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private refreshTokenPromise?: Promise<string>

  constructor(
    private http: HttpClient,
    private cookies: CookieService,
    private router: Router,
    handler: HttpBackend,
    private eventsService: EventsService,
    private notifications: NotificationService
  ) {
    this.httpNoAuth = new HttpClient(handler)
  }

  private httpNoAuth: HttpClient

  signin(email?: string | null, password?: string | null, mode?: string | null) {
    return this.http
      .post<LogInResponse>(`${environment.authApiUrl}/user/signin`, {
        email,
        password,
        mode
      })
      .pipe(
        catchError((e) => {
          if (e.status === 500) {
            return throwError(() => new Error('Unexpected error!'))
          }
        })
      )
  }

  logout(token: string) {
    return this.http
      .post<SignOutResponse>(
        `${environment.authApiUrl}/user/logout/${token}`,
        null
      )
      .pipe(
        catchError((e) => {
          if (
            e.status === 500 &&
            e.error.Message !== 'Access Token has been revoked'
          ) {
            return throwError(() => new Error('Unexpected error!'))
          }
        })
      )
  }

  forgotAccount(email: string) {
    return this.http
      .post<ForgotAccountResponse>(
        `${environment.authApiUrl}/user/forgot/${email}`,
        null
      )
      .pipe(
        catchError((e) => {
          if (e.status === 500) {
            return throwError(() => new Error('Unexpected error!'))
          }
        })
      )
  }

  forgotCode(email: string, newPassword: string, code: string) {
    return this.http.post<ForgotAccountCodeResponse>(
      `${environment.authApiUrl}/user/resetforgot`,
      {
        EmailAddress: email,
        NewPassword: newPassword,
        ConfirmationCode: code
      }
    )
  }

  refreshToken(token: string, email: string) {
    return this.httpNoAuth.post<LogInResponse>(
      `${environment.authApiUrl}/user/refreshtoken`,
      {
        EmailAddress: email,
        RefreshToken: token
      }
    )
  }

  async ensureValidAccessToken() {
    const accessToken = this.cookies.get('at')

    if (!accessToken) {
      throw new Error('No access token available')
    }

    const helper = new JwtHelperService()
    if (!helper.isTokenExpired(accessToken)) {
      return accessToken
    }

    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise
    }

    const identityToken = this.cookies.get('it')
    const refreshToken = this.cookies.get('rt')
    const claims = identityToken ? helper.decodeToken(identityToken) : null
    const email = claims?.email

    if (!refreshToken || !email) {
      this.handleExpiredSession()
      throw new Error('Session expired')
    }

    this.refreshTokenPromise = firstValueFrom(
      this.refreshToken(refreshToken, email)
    )
      .then((response) => {
        this.persistSessionTokens(response)

        return response.tokens.accessToken
      })
      .catch((error) => {
        this.handleExpiredSession()
        throw error
      })
      .finally(() => {
        this.refreshTokenPromise = undefined
      })

    return this.refreshTokenPromise
  }

  confirmUser(userId: string, email: string, code: string, password: string) {
    return this.http.post<ConfirmUserResponse>(
      `${environment.authApiUrl}/user/confirm`,
      {
        UserId: userId,
        EmailAddress: email,
        ConfirmationCode: code,
        NewPassword : password
      }
    )
  }

  getUserInfo(email: string) {
    return this.http.get<UserProfileResponse>(
      `${environment.authApiUrl}/user/profile/${email}`
    )
  }

  checkUserDomain(email: string) {
    return this.httpNoAuth.get<Company>(
      `${environment.apiUrl}/user/company/${email}`
    )
  }

  clearSession() {
    this.cookies.delete('at')
    this.cookies.delete('rt')
    this.cookies.delete('it')
    const langs = localStorage.getItem('langs')
    localStorage.clear()
    localStorage.setItem('langs', langs)
    this.router.navigate(['login'])
  }

  private persistSessionTokens(response: LogInResponse) {
    const {
      tokens: { idToken, accessToken, refreshToken }
    } = response

    this.cookies.delete('at')
    this.cookies.delete('it')
    this.cookies.delete('rt')
    this.cookies.set('at', accessToken, {
      secure: true,
      path: '/'
    })
    this.cookies.set('rt', refreshToken, {
      path: '/',
      secure: true
    })
    this.cookies.set('it', idToken, { secure: true, path: '/' })
  }

  private handleExpiredSession() {
    this.notifications.info('Session expired', 5000)
    this.eventsService.emit(new EventData('logout', null))
  }
}
